<template>
  <a-modal
    title="报告详情"
    :visible="visible"
    :footer="null"
    :mask-closable="false"
    wrap-class-name="wisc-report-detail-modal"
    @cancel="cancel"
  >
    <a-spin :spinning="loading">
      <div v-if="data" class="report-scroll">
        <header class="report-overview">
          <div class="overview-main">
            <div class="report-kicker">韦氏幼儿智力量表第四版</div>
            <h2>{{ displayValue(data.name) }}</h2>
            <div class="overview-meta">
              <span>{{ ageRange }}</span>
              <span>{{ displayValue(data.gender, genderOptions) }}</span>
              <span>实际年龄 {{ displayValue(data.real_age) }}</span>
              <span>测试日期 {{ displayValue(data.test_date) }}</span>
            </div>
          </div>
          <div class="report-number">
            <span>计分编号</span>
            <strong>{{ displayValue(data.scoring_num) }}</strong>
          </div>
        </header>

        <main class="report-content">
          <section class="report-section">
            <div class="section-heading">
              <span class="section-index">01</span>
              <h3>基本信息</h3>
            </div>
            <dl class="info-grid">
              <div v-for="item in basicInfoItems" :key="item.label" class="info-item">
                <dt>{{ item.label }}</dt>
                <dd>{{ item.value }}</dd>
              </div>
            </dl>
          </section>

          <section class="report-section">
            <div class="section-heading">
              <span class="section-index">02</span>
              <h3>分测验得分</h3>
              <span class="section-count">{{ scoreItems.length }} 项</span>
            </div>
            <div class="score-grid">
              <div v-for="item in scoreItems" :key="item.key" class="score-item">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </section>

          <section class="report-section settings-section">
            <div class="section-heading">
              <span class="section-index">03</span>
              <h3>评估参数</h3>
            </div>
            <dl class="settings-grid">
              <div v-for="item in settingItems" :key="item.label" class="setting-item">
                <dt>{{ item.label }}</dt>
                <dd>{{ item.value }}</dd>
              </div>
            </dl>
          </section>

          <section v-if="data.remark" class="report-section remark-section">
            <div class="section-heading">
              <span class="section-index">04</span>
              <h3>备注说明</h3>
            </div>
            <p>{{ data.remark }}</p>
          </section>

          <section v-if="hasDetailScores" class="report-section score-detail-section">
            <div class="section-heading">
              <span class="section-index">{{ data.remark ? '05' : '04' }}</span>
              <h3>各题详细分数</h3>
              <span class="section-count">{{ detailScoreCount }} 题</span>
            </div>
            <wisc-score-detail :value="detailScores" :edition="data.edition" readonly />
          </section>
        </main>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { Detail } from "../../commonApi";
import WiscScoreDetail from "../wisc-score-detail";

const AGE_RANGES = {
  1: "2:6 - 3:11",
  2: "4:0 - 6:11",
  3: "7:0 - 18:11",
};

const SCORE_FIELDS = {
  1: [
    ["score_rv", "指认图片"],
    ["score_bd", "积木"],
    ["score_pm", "图片记忆"],
    ["score_in", "常识"],
    ["score_oa", "拼图"],
    ["score_zl", "动物家园"],
    ["score_pn", "图片命名"],
  ],
  2: [
    ["score_bd", "积木"],
    ["score_in", "常识"],
    ["score_mr", "矩阵推理"],
    ["score_pm", "图片记忆"],
    ["score_si", "类同"],
    ["score_pc", "图画概念"],
    ["score_zl", "动物家园"],
    ["score_oa", "拼图"],
    ["score_rv", "指认图片"],
    ["score_pn", "图片命名"],
    ["score_bs", "找虫"],
    ["score_ca", "划消"],
    ["score_ac", "动物译码"],
    ["score_car", "随机划消"],
    ["score_cas", "有序划消"],
  ],
  3: [
    ["score_bd", "积木"],
    ["score_in", "常识"],
    ["score_mr", "矩阵推理"],
    ["score_pm", "图片记忆"],
    ["score_si", "类同"],
    ["score_oa", "拼图"],
    ["score_rv", "指认图片"],
    ["score_bs", "找虫"],
  ],
};

export default {
  components: { WiscScoreDetail },
  props: {
    item: Object,
    detailVisible: Boolean,
  },
  data() {
    return {
      visible: false,
      loading: false,
      id: "",
      data: null,
      genderOptions: { 1: "男", 2: "女" },
    };
  },
  computed: {
    ageRange() {
      return AGE_RANGES[this.data && this.data.edition] || "-";
    },
    basicInfoItems() {
      if (!this.data) return [];
      return [
        { label: "诊疗卡号", value: this.displayValue(this.data.archives_num) },
        { label: "主试姓名", value: this.displayValue(this.data.examiner_name) },
        { label: "出生日期", value: this.displayValue(this.data.born_date) },
        { label: "少数民族", value: this.displayValue(this.data.is_minorities, { 1: "否", 2: "是" }) },
        { label: "用手习惯", value: this.displayValue(this.data.hand_habit, { 1: "左手", 2: "右手" }) },
        { label: "所在年级", value: this.displayValue(this.data.grade) },
        { label: "家长文化", value: this.displayValue(this.data.parent_culture) },
        { label: "测试地点", value: this.displayValue(this.data.exam_address) },
      ];
    },
    scoreItems() {
      if (!this.data) return [];
      const fields = SCORE_FIELDS[this.data.edition] || [];
      return fields.map(([key, label]) => ({
        key,
        label,
        value: this.displayValue(this.data[key]),
      }));
    },
    settingItems() {
      if (!this.data) return [];
      return [
        { label: "置信区间", value: this.displayValue(this.data.confidence_probability) },
        { label: "显著性水平", value: this.displayValue(this.data.significance_level) },
        { label: "比较基础", value: this.displayValue(this.data.comparison_basis, { 1: "全样本", 2: "能力水平" }) },
        { label: "计算方法", value: this.displayValue(this.data.computing_method, { 1: "替代分测验", 2: "比例估算法" }) },
      ];
    },
    detailScores() {
      if (this.data && this.data.detail_scores && typeof this.data.detail_scores === "object") {
        return this.data.detail_scores;
      }
      if (this.data && this.data.detail_score_json) {
        try {
          return JSON.parse(this.data.detail_score_json);
        } catch (e) {
          return {};
        }
      }
      return {};
    },
    hasDetailScores() {
      return this.data
        && String(this.data.detail_score_enabled) === "1"
        && Object.keys(this.detailScores).length > 0;
    },
    detailScoreCount() {
      return Object.keys(this.detailScores).reduce((total, key) => {
        const scores = this.detailScores[key];
        return total + (scores && typeof scores === "object" ? Object.keys(scores).length : 0);
      }, 0);
    },
  },
  watch: {
    item: {
      handler(value) {
        if (!value) return;
        this.data = value;
        this.id = value.id || "";
      },
      immediate: true,
      deep: true,
    },
    detailVisible: {
      handler(value) {
        this.visible = value;
        if (value) this.getDetail();
      },
      immediate: true,
    },
  },
  methods: {
    displayValue(value, options) {
      if (options && Object.prototype.hasOwnProperty.call(options, value)) {
        return options[value];
      }
      return value === 0 || value ? value : "-";
    },
    async getDetail() {
      if (!this.id) return;
      this.loading = true;
      try {
        const res = await Detail({ id: this.id });
        if (res && res.code == 0) {
          this.data = res.wiscAssessment;
        }
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.visible = false;
      this.$emit("detailCancel");
    },
  },
};
</script>

<style scoped>
.report-scroll {
  max-height: 780px;
  max-height: 78vh;
  overflow-y: auto;
  background: #fff;
}

.report-overview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  min-height: 168px;
  padding: 32px 40px;
  color: #fff;
  background: #54214d;
  border-bottom: 5px solid #2f746c;
}

.overview-main {
  min-width: 0;
}

.report-kicker {
  margin-bottom: 10px;
  color: #dfc8db;
  font-size: 13px;
  font-weight: 500;
}

.report-overview h2 {
  margin: 0 0 16px;
  color: #fff;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;
}

.overview-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  color: #f3eaf1;
  font-size: 13px;
}

.report-number {
  flex: 0 0 218px;
  padding-left: 28px;
  border-left: 1px solid rgba(255, 255, 255, 0.25);
}

.report-number span {
  display: block;
  margin-bottom: 7px;
  color: #dfc8db;
  font-size: 12px;
}

.report-number strong {
  display: block;
  color: #fff;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
}

.report-content {
  padding: 8px 40px 40px;
}

.report-section {
  padding-top: 30px;
}

.report-section + .report-section {
  margin-top: 30px;
  border-top: 1px solid #e8e9eb;
}

.section-heading {
  display: flex;
  align-items: center;
  min-height: 28px;
  margin-bottom: 20px;
}

.section-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 24px;
  margin-right: 11px;
  color: #fff;
  background: #2f746c;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.section-heading h3 {
  margin: 0;
  color: #25272b;
  font-size: 18px;
  font-weight: 600;
}

.section-count {
  margin-left: auto;
  color: #7a7f87;
  font-size: 13px;
}

.info-grid,
.settings-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
}

.info-item,
.setting-item {
  min-width: 0;
  min-height: 72px;
  margin: 0;
  padding: 13px 18px;
  border-bottom: 1px solid #e8e9eb;
  border-left: 1px solid #e8e9eb;
}

.info-item:nth-child(-n + 4),
.setting-item:nth-child(-n + 4) {
  border-top: 1px solid #e8e9eb;
}

.info-item:nth-child(4n),
.setting-item:nth-child(4n) {
  border-right: 1px solid #e8e9eb;
}

.info-item dt,
.setting-item dt {
  margin-bottom: 6px;
  color: #81858c;
  font-size: 12px;
  line-height: 1.4;
}

.info-item dd,
.setting-item dd {
  margin: 0;
  color: #303238;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  word-break: break-word;
}

.score-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.score-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  min-height: 76px;
  padding: 14px 16px;
  background: #f7f7f8;
  border: 1px solid #e7e7ea;
  border-left: 3px solid #832c75;
  border-radius: 4px;
}

.score-item span {
  color: #595c63;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
}

.score-item strong {
  flex: 0 0 auto;
  color: #54214d;
  font-size: 24px;
  font-weight: 600;
}

.settings-section .settings-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.remark-section p {
  margin: 0;
  padding: 16px 18px;
  color: #4f5258;
  background: #f7f7f8;
  border-left: 3px solid #c48a32;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}

.score-detail-section /deep/ .detail-score-panel {
  margin: 0;
}

@media (max-width: 820px) {
  .report-overview {
    align-items: flex-start;
    padding: 26px 24px;
  }

  .report-number {
    flex-basis: 180px;
  }

  .report-content {
    padding: 4px 24px 30px;
  }

  .info-grid,
  .settings-grid,
  .settings-section .settings-grid,
  .score-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .info-item:nth-child(-n + 4),
  .setting-item:nth-child(-n + 4) {
    border-top: 0;
  }

  .info-item:nth-child(-n + 2),
  .setting-item:nth-child(-n + 2) {
    border-top: 1px solid #e8e9eb;
  }

  .info-item:nth-child(4n),
  .setting-item:nth-child(4n) {
    border-right: 0;
  }

  .info-item:nth-child(2n),
  .setting-item:nth-child(2n) {
    border-right: 1px solid #e8e9eb;
  }
}

@media (max-width: 560px) {
  .report-scroll {
    max-height: calc(100vh - 92px);
  }

  .report-overview {
    display: block;
    min-height: 0;
    padding: 24px 20px;
  }

  .report-overview h2 {
    font-size: 23px;
  }

  .report-number {
    margin-top: 22px;
    padding: 16px 0 0;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
    border-left: 0;
  }

  .report-content {
    padding: 0 18px 26px;
  }

  .report-section,
  .report-section + .report-section {
    margin-top: 24px;
    padding-top: 24px;
  }

  .report-section:first-child {
    margin-top: 0;
  }

  .info-grid,
  .settings-grid,
  .settings-section .settings-grid,
  .score-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .info-item,
  .setting-item,
  .info-item:nth-child(-n + 2),
  .setting-item:nth-child(-n + 2),
  .info-item:nth-child(2n),
  .setting-item:nth-child(2n) {
    border-top: 0;
    border-right: 1px solid #e8e9eb;
  }

  .info-item:first-child,
  .setting-item:first-child {
    border-top: 1px solid #e8e9eb;
  }
}
</style>

<style>
.wisc-report-detail-modal .ant-modal {
  width: 1040px !important;
  max-width: calc(100vw - 24px);
  padding-bottom: 0;
}

.wisc-report-detail-modal .ant-modal-content {
  overflow: hidden;
  border-radius: 6px;
}

.wisc-report-detail-modal .ant-modal-header {
  padding: 17px 24px;
  border-bottom-color: #e8e9eb;
}

.wisc-report-detail-modal .ant-modal-title {
  color: #303238;
  font-size: 16px;
  font-weight: 600;
}

.wisc-report-detail-modal .ant-modal-body {
  padding: 0;
}

.wisc-report-detail-modal .ant-spin-nested-loading,
.wisc-report-detail-modal .ant-spin-container {
  min-height: 180px;
}

@media (max-width: 560px) {
  .wisc-report-detail-modal .ant-modal {
    top: 12px;
    max-width: calc(100vw - 16px);
    margin: 0 auto;
  }
}
</style>
